/**
 * 视频生成 Services
 */

import { pollUntilCompleted } from "@/utils/pollUntilTaskCompleted";
import {
  DataValidator,
  IsNumber,
  IsString,
  Required,
  transformKeys,
} from "@/utils/type-vaildator";
import api, { apiV2 } from "./api";

const PROCESS_STATUS = {
  '1': '处理中',
  '2': '处理成功',
  '3': 'process failed', // 处理错误
  '4': 'process timeout',// 处理超时
} as const;

/** 生成音频请求参数 */
export interface GenerateAudioProps {
  audioName: string;
  audioUrl: string;
  onError?: (error: Error) => void;
  point?: number;
}

/** 生成音频响应参数 */
export interface GenerateAudioResponse {
  created_at: string;
  status: string;
  task_id: string;
  updated_at: string;
}

export class GenerateAudioData {
  @Required("createdAt cannot be empty")
  @IsString("createdAt must be a string")
  createdAt: string;

  @Required("status cannot be empty")
  @IsString("status must be a string")
  status: string;

  @Required("taskId cannot be empty")
  @IsString("taskId must be a string")
  taskId: string;
}

/** 音频生成状态响应参数 */
export interface PollAudioStatusResponse {
  process_status: number;
  sample_url: string;
  task_id: string;
  input_text: string;
}

export class PollAudioStatusProcessData {
  @Required("processStatus cannot be empty")
  @IsNumber("processStatus must be a number")
  processStatus: number;

  @Required("taskId cannot be empty")
  @IsString("taskId must be a string")
  taskId: string;
}

export class PollAudioStatusFailData {
  @Required("processStatus cannot be empty")
  @IsNumber("processStatus must be a number")
  processStatus: number;

  @Required("taskId cannot be empty")
  @IsString("taskId must be a string")
  taskId: string;
}

export class PollAudioStatusData {
  @Required("processStatus cannot be empty")
  @IsNumber("processStatus must be a number")
  processStatus: number;

  @Required("sampleUrl cannot be empty")
  @IsString("sampleUrl must be a string")
  sampleUrl: string;

  @Required("taskId cannot be empty")
  @IsString("taskId must be a string")
  taskId: string;
}

const transformAndValidatePollAudioStatusData = (
  response: GenerateAudioResponse
): GenerateAudioData => {
  const transformedData = transformKeys(response);

  try {
    const mainData = DataValidator.validateOrThrow(
      transformedData,
      GenerateAudioData
    );
    return mainData;
  } catch (error) {
    console.error("Generate audio data validation failed:", error.message);
    throw error;
  }
};

const transformAndValidatePollAudioStatus = (
  response: PollAudioStatusResponse
): PollAudioStatusData => {
  const transformedData = transformKeys(response);

  try {
    // 根据生成状态校验响应数据
    if (transformedData.processStatus === 1) {
      DataValidator.validateOrThrow(
        transformedData,
        PollAudioStatusProcessData
      );
    } else if (transformedData.processStatus === 2) {
      DataValidator.validateOrThrow(
        transformedData,
        PollAudioStatusData
      )
    } else {
      DataValidator.validateOrThrow(
        transformedData,
        PollAudioStatusFailData
      );
    }
    return transformedData;
  } catch (error) {
    console.error("Poll audio status data validation failed:", error.message);
    throw error;
  }
};

/**
 * 生成 Elevenlabs 音频
 * @param audioName 音频名称
 * @param audioUrl 音频url
 * @returns Promise<GenerateAudioResponse>
 */
export const generateElevenLabsAudio = async ({
  audioName,
  audioUrl,
}: GenerateAudioProps) => {
  // 发起生成请求
  const response = await api.post<GenerateAudioResponse>(
    "/audio/clone-elevenlabs",
    {
      audio_name: audioName,
      audio_url: audioUrl,
    }
  );
  const data = transformAndValidatePollAudioStatusData(response.data);
  // 轮询获取生成状态直到完成
  const result = await pollUntilCompleted<PollAudioStatusData>(() =>
    pollAudioStatus(data.taskId)
  );
  return result;
};

/**
 * 生成 Minimax 音频
 * @param audioName 音频名称
 * @param audioUrl 音频url
 * @returns Promise<GenerateAudioResponse>
 */
export const generateMinimaxAudio = async ({
  userId,
  audioName,
  audioUrl,
  point,
}: GenerateAudioProps & { userId: string }) => {
  // 发起生成请求
  const response = await apiV2.post<GenerateAudioResponse>(
    "/audio/clone-minimax",
    {
      user_id: userId,
      audio_name: audioName,
      audio_url: audioUrl,
      point,
    }
  );
  const data = transformAndValidatePollAudioStatusData(response.data);
  // 轮询获取生成状态直到完成
  const result = await pollUntilCompleted<PollAudioStatusData>(() =>
    pollAudioStatus(data.taskId)
  );
  return result;
};

/**
 * 音频生成状态
 * @param taskId
 * @returns
 */
export const pollAudioStatus = async (taskId: string) => {
  const response = await api.get<PollAudioStatusResponse>(
    `/audio/processing-status/${taskId}`
  );
  const data = transformAndValidatePollAudioStatus(response.data);
  // 0-未处理，1-处理中，2-处理成功，3-处理失败，4-处理超时
  if (data.processStatus > 1) {
    if ([3, 4].includes(data.processStatus)) {
      throw new Error(`Audio clone ${PROCESS_STATUS[data.processStatus]}`);
    }
    return data;
  }
  return null;
};
